import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useCreateBillableItem, useUpdateBillableItem } from '@/hooks';
import {
  billableItemCreateSchema,
  type BillableItemCreate,
  type BillableItem,
} from '@/lib/schemas';
import { calculateSuggestedTax, formatTaxSuggestion } from '@/lib/tax-utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MonthPicker } from '@/components/ui/month-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CategorySelect } from '@/components/billable-items/CategorySelect';
import type { BillableItemCategory } from '@/components/billable-items/CategorySelect';

interface BillableItemFormProps {
  item?: BillableItem;
  projectId: string;
  projectNumber: string | null;  // Phase 9.6a: Changed from number to string, null for proposals/templates
  mode: 'create' | 'edit';
  workflowStage?: string; // Phase 7.5.3: Workflow-dependent fee type restrictions
  isTemplate?: boolean; // Phase 7.5.3: Templates can use all categories
}

export function BillableItemForm({
  item,
  projectId,
  projectNumber,
  mode,
  workflowStage,
  isTemplate,
}: BillableItemFormProps) {
  const navigate = useNavigate();
  const createItem = useCreateBillableItem();
  const updateItem = useUpdateBillableItem();

  // Phase 7.5.3 fix: the create-mode default category must never be a category
  // CategorySelect is about to render disabled for this workflowStage (see
  // getCategoryDisabledReason in CategorySelect.tsx — the same three conditions
  // are mirrored here). Professional Fee is only the default during proposal
  // stages (or when there's no workflowStage/isTemplate restriction at all);
  // everywhere else the default is Add Service, which — like Professional Fee —
  // is a revenue category (5% GST via src/lib/taxRates.ts), so switching the
  // default away from Professional Fee never silently drops the line's tax.
  const isProposalStage = ['proposal_draft', 'proposal_sent', 'proposal_declined'].includes(workflowStage || '');
  const defaultCreateCategory: BillableItemCategory =
    !isTemplate && workflowStage && !isProposalStage ? 'add_service' : 'professional_fee';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BillableItemCreate>({
    resolver: zodResolver(billableItemCreateSchema),
    defaultValues: item
      ? {
          project_id: item.project_id,
          project_number: item.project_number || projectNumber,
          description: item.description,
          amount: item.amount,
          category: item.category || 'professional_fee', // Phase 5 Batch 4: category determines tax at invoice time
          cancelled: item.cancelled, // Migration 013: replaced status with cancelled boolean
          status: item.status || 'not_started', // Phase 7 Batch 2: status tracking
          status_notes: item.status_notes || '', // Phase 7 Batch 2: status notes
          // Convert YYYY-MM-DD to YYYY-MM for month input
          scheduled_month: item.scheduled_month ? item.scheduled_month.slice(0, 7) : '', // Phase 7.5.12: expected invoice month
          // 10c.2: Recurring partnership fee fields
          amount_recurring: item.amount_recurring ?? null,
          recurring_interval: item.recurring_interval ?? null,
        }
      : {
          project_id: projectId,
          project_number: projectNumber,
          category: defaultCreateCategory, // Phase 7.5.3 fix: workflow-aware default (was hardcoded 'professional_fee')
          cancelled: false, // Migration 013: default to not cancelled
          status: 'not_started', // Phase 7 Batch 2: default status
          status_notes: '', // Phase 7 Batch 2: default empty notes
          scheduled_month: '', // Phase 7.5.12: default empty
          // 10c.2: Recurring partnership fee fields
          amount_recurring: null,
          recurring_interval: null,
        },
  });

  // Phase 5 Batch 4: Watch category and amount to show tax suggestion (informational only)
  // Phase 7 Batch 2: Watch category to handle internal_work (must be $0)
  const category = watch('category');
  const amount = watch('amount');
  // 10c.2: Watch recurring amount to conditionally show interval dropdown
  const amountRecurring = watch('amount_recurring');

  // Phase 7 Batch 2: Auto-set amount to $0 when Internal Work selected
  useEffect(() => {
    if (category === 'internal_work') {
      setValue('amount', 0);
    }
  }, [category, setValue]);

  // Issue #3: Calculate whether to show tax information (prevent NaN from displaying)
  // Phase 7 Batch 2: Don't show tax info for internal_work (always $0)
  const shouldShowTaxInfo = category && category !== 'internal_work' && typeof amount === 'number' && !isNaN(amount) && amount > 0;

  const onSubmit = async (data: BillableItemCreate) => {
    try {
      if (mode === 'create') {
        await createItem.mutateAsync(data);
        navigate(`/projects/${projectId}`);
      } else if (item) {
        // Strip project_id and project_number from update payload -
        // these are create-only fields. Sending project_id triggers
        // a super-admin-only reassignment check on the backend.
        const { project_id, project_number, ...updateData } = data;
        await updateItem.mutateAsync({ id: item.id, data: updateData });
        navigate(`/projects/${projectId}`);
      }
    } catch (error) {
      // Mutation hooks handle error toasts via onError - just log for debugging
      console.error('Form submission error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Billable Item Details</CardTitle>
          <CardDescription>
            {mode === 'create'
              ? 'Add confirmed work to this project'
              : 'Update billable item information'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-destructive">*</span>
            </Label>
            <textarea
              id="description"
              {...register('description')}
              rows={4}
              className={`flex w-full rounded-md border ${
                errors.description ? 'border-destructive' : 'border-input'
              } bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
              placeholder="Describe the billable work"
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>

          {/* Amount - Hide for Internal Work (Phase 7 Batch 2) */}
          {category !== 'internal_work' && (
            <div className="space-y-2">
              <Label htmlFor="amount">
                Amount <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  {...register('amount', { valueAsNumber: true })}
                  className={`pl-7 ${errors.amount ? 'border-destructive' : ''}`}
                  placeholder="0.00"
                  onFocus={(e) => e.target.select()}
                />
              </div>
              {errors.amount && (
                <p className="text-sm text-destructive">{errors.amount.message}</p>
              )}
            </div>
          )}

          {/* Internal Work Info (Phase 7 Batch 2) */}
          {category === 'internal_work' && (
            <div className="rounded-lg border border-muted bg-muted/50 p-3">
              <p className="text-sm font-medium mb-1">Internal Work Tracking</p>
              <p className="text-sm text-muted-foreground">
                Internal work items are always $0 (not billable). Use status and notes below to track progress.
              </p>
            </div>
          )}

          {/* 10c.2: Recurring/Partnership Fee (proposal-only, never invoiced) */}
          {category !== 'internal_work' && (
            <div className="space-y-3 rounded-lg border border-muted p-3">
              <p className="text-sm font-medium">Recurring / Partnership Fee <span className="text-muted-foreground font-normal">(optional — shows on proposals only, not invoiced)</span></p>
              <div className="space-y-2">
                <Label htmlFor="amount_recurring">Recurring Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="amount_recurring"
                    type="number"
                    step="0.01"
                    {...register('amount_recurring', { valueAsNumber: true, setValueAs: (v) => (v === '' || isNaN(v) ? null : Number(v)) })}
                    className={`pl-7 ${errors.amount_recurring ? 'border-destructive' : ''}`}
                    placeholder="0.00"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                {errors.amount_recurring && (
                  <p className="text-sm text-destructive">{errors.amount_recurring.message}</p>
                )}
              </div>
              {/* Interval dropdown — only show when recurring amount > 0 */}
              {amountRecurring != null && amountRecurring > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="recurring_interval">Billing Interval <span className="text-destructive">*</span></Label>
                  <Select
                    value={watch('recurring_interval') || ''}
                    onValueChange={(value) => setValue('recurring_interval', value as 'monthly' | 'quarterly' | 'annual', { shouldValidate: true })}
                  >
                    <SelectTrigger id="recurring_interval" className={errors.recurring_interval ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select interval" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.recurring_interval && (
                    <p className="text-sm text-destructive">{errors.recurring_interval.message}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Phase 5 Batch 4: Category Selection */}
          <div className="space-y-2">
            <Label htmlFor="category">
              Category *
            </Label>
            <CategorySelect
              id="category"
              value={category as BillableItemCategory}
              onChange={(value) => setValue('category', value)}
              workflowStage={workflowStage}
              isTemplate={isTemplate}
            />
            {errors.category && (
              <p className="text-sm text-destructive">{errors.category.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Category determines tax treatment and revenue classification
            </p>
          </div>

          {/* Phase 7.5.12: Expected Invoice Month (Cash Flow Planning) */}
          <div className="space-y-2">
            <Label htmlFor="scheduled_month">Expected Invoice Month</Label>
            <MonthPicker
              id="scheduled_month"
              value={watch('scheduled_month') || null}
              onChange={(value) => setValue('scheduled_month', value, { shouldValidate: true })}
              placeholder="Select month"
              className={errors.scheduled_month ? 'border-destructive' : ''}
            />
            {errors.scheduled_month && (
              <p className="text-sm text-destructive">{errors.scheduled_month.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              When you expect to invoice this item. Used for cash flow forecasting and the 'Overdue' filter.
            </p>
          </div>

          {/* Phase 7 Batch 2: Status Tracking */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={watch('status')}
              onValueChange={(value) => setValue('status', value as 'not_started' | 'in_progress' | 'complete' | 'on_hold')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            {errors.status && (
              <p className="text-sm text-destructive">{errors.status.message}</p>
            )}
          </div>

          {/* Phase 7 Batch 2: Status Notes */}
          <div className="space-y-2">
            <Label htmlFor="status_notes">Status Notes</Label>
            <textarea
              id="status_notes"
              {...register('status_notes')}
              rows={3}
              className={`flex w-full rounded-md border ${
                errors.status_notes ? 'border-destructive' : 'border-input'
              } bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
              placeholder="Add notes about current status (optional)"
            />
            {errors.status_notes && (
              <p className="text-sm text-destructive">{errors.status_notes.message}</p>
            )}
          </div>

          {/* Migration V011: Due Date Tracking */}
          <div className="space-y-2">
            <Label htmlFor="due_date">Due Date</Label>
            <Input
              id="due_date"
              type="date"
              {...register('due_date')}
              className={errors.due_date ? 'border-destructive' : ''}
            />
            {errors.due_date && (
              <p className="text-sm text-destructive">{errors.due_date.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Optional deadline for tracking urgency (internal work, permits, etc.)
            </p>
          </div>

          {/* Phase 5 Batch 4: Tax Information (calculated at invoice time) */}
          {/* Issue #3: Only show tax info when amount is a valid number > 0 (prevent NaN from displaying) */}
          {shouldShowTaxInfo && (
            <div className="rounded-lg border border-muted bg-muted/50 p-3">
              <p className="text-sm font-medium mb-1">Tax Information</p>
              <p className="text-sm text-muted-foreground">
                {formatTaxSuggestion(
                  calculateSuggestedTax(amount, category as BillableItemCategory),
                  category as BillableItemCategory
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Tax will be calculated when this item is added to an invoice
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex items-center gap-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? mode === 'create'
              ? 'Creating...'
              : 'Saving...'
            : mode === 'create'
              ? 'Create Billable Item'
              : 'Save Changes'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(`/projects/${projectId}`)}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
